import {
  CarouselAnalyticsComponentService,
  ProductListAnalyticsService,
  RetailRocketService,
} from '@analytics/feature';
import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CityService } from '@city/feature';
import { ReferencesCmsComponents } from '@core/const';
import { EnvironmentService } from '@core/feature';
import { CustomProduct, RetailRocketReferencesCmsComponentData } from '@product/const';
import { RetailRocketCarouselFacade, RetailRocketCarouselStateService } from '@reference/feature';
import { ProductListReviewsService } from '@reviews/data-access';
import { CustomProductReferencesComponent } from '@sdvor/product/ui';
import { DeviceService, UserAgentService } from '@shared/services';
import { ProductReferenceService } from '@spartacus/core';
import { CmsComponentData, CurrentProductService } from '@spartacus/storefront';
import { combineLatest, iif, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { SwiperOptions } from 'swiper/types';

@Component({
  selector: 'sd-retail-rocket-references',
  templateUrl: './retail-rocket-references.component.html',
  styleUrls: ['./retail-rocket-references.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { 'attr.data-testid': 'product-carousel-rr' },
  providers: [
    CarouselAnalyticsComponentService,
    {
      provide: ProductListAnalyticsService,
      useExisting: CarouselAnalyticsComponentService,
    },
    RetailRocketCarouselStateService,
  ],
  standalone: false,
})
export class RetailRocketReferencesComponent extends CustomProductReferencesComponent implements OnInit, OnDestroy {
  items$: Observable<Observable<CustomProduct>[] | null>;
  carouselTitle$: Observable<string>;

  config: SwiperOptions = {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 5,
    scrollbar: { enabled: false },
  };

  loading$: Observable<boolean>;

  constructor(
    protected cmsComponentData: CmsComponentData<RetailRocketReferencesCmsComponentData>,
    current: CurrentProductService,
    referenceService: ProductReferenceService,
    protected deviceService: DeviceService,
    protected userAgentService: UserAgentService,
    protected cityService: CityService,
    protected carouselAnalyticsComponentService: CarouselAnalyticsComponentService,
    protected retailRocketCarouselFacade: RetailRocketCarouselFacade,
    protected retailRocketCarouselStateService: RetailRocketCarouselStateService,
    productListReviewsService: ProductListReviewsService,
    protected retailRocketService: RetailRocketService,
    environmentService: EnvironmentService,
  ) {
    super(
      cmsComponentData,
      current,
      referenceService,
      deviceService,
      userAgentService,
      cityService,
      carouselAnalyticsComponentService,
      productListReviewsService,
      environmentService,
    );
  }

  ngOnInit(): void {
    const sizes = this.deviceService.sizes;
    this.config.breakpoints = {
      [1]: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 10,
        navigation: false,
      },
      [375]: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 10,
      },
      [sizes.sm]: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        spaceBetween: 10,
      },
      [sizes.md]: {
        slidesPerView: 4,
        slidesPerGroup: 4,
        spaceBetween: 10,
      },
      [sizes.lg]: {
        slidesPerView: 5,
        slidesPerGroup: 5,
        spaceBetween: 10,
      },
    };

    this.init();
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.retailRocketCarouselFacade.resetRecomendations(this.cmsComponentData.uid);
  }

  onIndexChange(index: number): void {
    this.carouselAnalyticsComponentService.setOffset(index);
  }

  onSlidesPerViewChange(slidesPerView: number): void {
    this.carouselAnalyticsComponentService.setSlidesPerView(slidesPerView);
  }

  onIntersect(): void {
    this.carouselAnalyticsComponentService.setIntersected();
  }

  protected get componentData$(): Observable<RetailRocketReferencesCmsComponentData> {
    return iif(
      () => this.environmentService.isPlatformServer,
      this.cmsComponentData.data$.pipe(
        distinctUntilChanged((a, b) => {
          return a && b && a.algorithms === b.algorithms && a.maximumNumberProducts === b.maximumNumberProducts;
        }),
        switchMap((data) => this.cityService.activeBaseStoreCode$.pipe(map(() => data))),
        filter<RetailRocketReferencesCmsComponentData>(Boolean),
      ),
      this.cmsComponentData.data$.pipe(
        distinctUntilChanged((a, b) => {
          return a && b && a.algorithms === b.algorithms && a.maximumNumberProducts === b.maximumNumberProducts;
        }),
        switchMap((data) => this.getUpdateConext(data).pipe(map(() => data))),
        filter<RetailRocketReferencesCmsComponentData>(Boolean),
      ),
    );
  }

  private init(): void {
    this.loading$ = this.retailRocketCarouselFacade.getRecomendationsLoading(this.cmsComponentData.uid);

    this.items$ = this.componentData$.pipe(
      switchMap((data) => {
        return this.retailRocketService.getSessionId().pipe(
          filter((sessionId) => Boolean(sessionId)),
          map(() => data),
        );
      }),
      map(({ typeCode, algorithms, maximumNumberProducts }) => ({
        typeCode,
        algorithms,
        maximumNumberProducts,
      })),
      filter<
        Required<Pick<RetailRocketReferencesCmsComponentData, 'algorithms' | 'typeCode' | 'maximumNumberProducts'>>
      >(
        ({ typeCode, algorithms, maximumNumberProducts }) =>
          Boolean(typeCode) && Boolean(algorithms) && maximumNumberProducts !== undefined,
      ),
      switchMap(({ typeCode, algorithms: cmsAlgorithms, maximumNumberProducts }) => {
        const algorithms = this.mapAlgorithms(cmsAlgorithms);

        this.retailRocketCarouselFacade.loadRecomendations(this.cmsComponentData.uid, typeCode, algorithms);
        this.retailRocketCarouselStateService.init(algorithms);
        this.carouselAnalyticsComponentService.init(`${this.mapAnaliticsName(typeCode)}_${algorithms}`, this.products$);

        return this.retailRocketCarouselFacade.getRecomendations(this.cmsComponentData.uid, maximumNumberProducts).pipe(
          map((items) => items || []),
          map(this.filterBigImg),
          tap((items) => {
            if (items.length) {
              this.loadReviews(items);
            }
          }),
        );
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.carouselTitle$ = this.cmsComponentData.data$.pipe(map((data) => data?.title));

    this.products$ = this.items$.pipe(switchMap((products$: Observable<CustomProduct>[]) => combineLatest(products$)));
  }

  private mapAnaliticsName(typeCode: ReferencesCmsComponents): string {
    switch (typeCode) {
      case ReferencesCmsComponents.RetailRocketProductReferencesComponent:
        return 'RR_ProductCard';
      case ReferencesCmsComponents.RetailRocketCartReferencesComponent:
        return 'RR_Cart';
      case ReferencesCmsComponents.RetailRocketUserReferencesComponent:
        return 'RR_User';
      case ReferencesCmsComponents.RetailRocketCatalogReferencesComponent:
        return 'RR_Catalog';
      case ReferencesCmsComponents.RetailRocketWishListReferencesComponent:
        return 'RR_WishList';
      case ReferencesCmsComponents.RetailRocketComparisonReferencesComponent:
        return 'RR_Comparison';
      default:
        return 'RR_ProductReferences';
    }
  }

  private mapAlgorithms(algorithms: string): string {
    return algorithms.replace(/ /g, ',');
  }

  private getUpdateConext(data: RetailRocketReferencesCmsComponentData): Observable<unknown> {
    return combineLatest([
      this.cityService.activeBaseStoreCode$.pipe(
        distinctUntilChanged(),
        // при изменении города, список товаров в карусели не успевает обновиться,
        // в этом случае рр запрашивает данные для старого списка, поэтому ждем 1 сек.
        debounceTime(1000),
      ),
      this.retailRocketCarouselFacade
        .getProductCodesByType(data.typeCode)
        .pipe(
          distinctUntilChanged(
            (productCodesPrev, productCodes) => JSON.stringify(productCodesPrev) === JSON.stringify(productCodes),
          ),
        ),
    ]);
  }
}
